import { initContract } from "@ts-rest/core";
import { z } from "zod";

import {
  FindOneQuoteRequestLineOutputSchema,
  FindOneQuoteRequestLineWithLatestJobSchema,
  FindOneQuoteRequestLineWithoutCosineSchema,
} from "../dtos/FindOneQuoteRequestLineOutputSchema";
import {
  BatchSchema,
  ClientSchema,
  CustomInputSchema,
  FileSchema,
  IndustrialProcessSchema,
  LayoutSchema,
  MaterialGradeSchema,
  MaterialLayoutSchema,
  NestingRunSchema,
  PartSchema,
  PartTypeSchema,
  PredictionSchema,
  ProcessStepModeSchema,
  ProcessStepSchema,
  ProductLineSchema,
  ProductSchema,
  QuoteRequestLineOptionalDefaultsSchema,
  QuoteRequestLineSchema,
  QuoteRequestSchema,
} from "../generated/prisma/zod";
import { ExtendedPartSchema } from "../types";
import { FilterGroupSchema } from "./partsContract";

const c = initContract();

const CustomInputSelectOptionalSchema = z.object({
  label: z.string(),
  value: z.string(),
});

const CustomInputGlobalSchema = z.object({
  index: z.number(),
  modelVersion: z.string(),
  key: z.string(),
  label: z.string(),
  relativeTo: z.string().nullable().optional(),
});

const CustomInputNumberSchema = CustomInputGlobalSchema.extend({
  type: z.literal("number"),
  defaultValue: z.number().nullable().optional(),
});

const CustomInputSelectSchema = CustomInputGlobalSchema.extend({
  type: z.literal("select"),
  options: z.array(CustomInputSelectOptionalSchema),
  defaultValue: z.string().nullable().optional(),
});

export const CustomInputResponseSchema = z.discriminatedUnion("type", [
  CustomInputNumberSchema,
  CustomInputSelectSchema,
]);

const singleReliablePartsResponseSchema = PartSchema.pick({ id: true, companyId: true })
  .extend({
    cosineSimilarity: z.number().optional(),
    quoteRequestLines: QuoteRequestLineSchema.pick({ id: true }).array(),
  })
  .array();

export type SingleReliablePartsResponse = z.infer<typeof singleReliablePartsResponseSchema>;
export const SingleReliablePartsResponseSchema: z.ZodType<SingleReliablePartsResponse> =
  singleReliablePartsResponseSchema;

const searchQuoteRequestLinesResponseSchema = z.object({
  quoteRequestLinesCount: z.number(),
  quoteRequestLinesCountPerPage: z.number(),
  allQuoteRequestLinesCount: z.number(),
  quoteRequestLines: QuoteRequestLineSchema.merge(
    z.object({
      quoteRequest: QuoteRequestSchema.merge(
        z.object({
          isFavorite: z.boolean().nullable(),
          client: ClientSchema.nullable(),
        }),
      ),
      part: ExtendedPartSchema.merge(
        z.object({
          files: FileSchema.array().nullish(),
          type: PartTypeSchema.nullable(),
        }),
      ),
      layout: LayoutSchema.merge(
        z.object({
          materialLayout: MaterialLayoutSchema.nullable(),
        }),
      ).nullable(),
      nestingStatus: NestingRunSchema.shape.status.nullable().optional(),
      processSteps: ProcessStepSchema.merge(
        z.object({
          productLines: ProductLineSchema.extend({
            product: ProductSchema,
          })
            .array()
            .nullish(),
        }),
      ).array(),
    }),
  ).array(),
});

type SearchQuoteRequestLinesResponse = z.infer<typeof searchQuoteRequestLinesResponseSchema>;
const SearchQuoteRequestLinesResponseSchema: z.ZodType<SearchQuoteRequestLinesResponse> =
  searchQuoteRequestLinesResponseSchema;

const duplicateQuoteRequestLineResponseSchema = QuoteRequestLineSchema.merge(
  z.object({
    part: ExtendedPartSchema.merge(
      z.object({
        files: FileSchema.array().nullish(),
      }),
    ),
  }),
);

type DuplicateQuoteRequestLineResponse = z.infer<typeof duplicateQuoteRequestLineResponseSchema>;
const DuplicateQuoteRequestLineResponseSchema: z.ZodType<DuplicateQuoteRequestLineResponse> =
  duplicateQuoteRequestLineResponseSchema;

export const quoteRequestLinesContract = c.router({
  findOne: {
    method: "GET",
    path: "/quote-request-lines/:id",
    responses: {
      200: FindOneQuoteRequestLineWithLatestJobSchema,
    },
  },

  findCustomInputs: {
    method: "GET",
    path: "/quote-request-lines/:id/custom-inputs",
    responses: {
      200: CustomInputSchema.array(),
    },
  },

  findChildren: {
    method: "GET",
    path: "/quote-request-lines/:id/children",
    responses: {
      200: FindOneQuoteRequestLineWithoutCosineSchema.array(),
    },
  },

  findPrediction: {
    method: "GET",
    path: "/quote-request-lines/:id/prediction",
    responses: {
      200: PredictionSchema.omit({
        apiResponse: true,
        apiRequest: true,
      }).nullable(),
    },
  },

  reorder: {
    method: "PATCH",
    path: "/quote-request-lines/reorder",
    body: z.object({
      quoteRequestId: z.string().uuid(),
      orderedIds: z.array(z.string().uuid()),
    }),
    responses: {
      200: z.object({
        updatedCount: z.number(),
      }),
    },
    summary: "Reorder quote request lines in a single atomic operation",
  },

  update: {
    method: "PATCH",
    path: "/quote-request-lines/:id",
    body: QuoteRequestLineSchema.omit({ id: true }).partial(),
    responses: {
      200: QuoteRequestLineSchema,
    },
  },

  setBatchReferenceLine: {
    method: "PATCH",
    path: "/quote-request-lines/:id/batch-reference-line",
    body: z.object({}).optional(),
    responses: {
      200: BatchSchema,
    },
    summary: "Make a quote request line the batch line the optimizations are computed on",
  },

  searchSimilarParts: {
    method: "GET",
    path: "/quote-request-lines/parts/:id/similar-parts",
    query: z.object({
      limit: z.number().optional().default(9),
      search: z.string().optional(),
      filterGroup: FilterGroupSchema,
    }),
    responses: {
      200: FindOneQuoteRequestLineOutputSchema.array(),
    },
    summary: "Search for similarParts quoteRequestLines from part with filters",
  },
  searchReliables: {
    method: "GET",
    path: "/quote-request-lines/:id/search-reliables",
    query: z.object({
      search: z.string().optional(),
      filterGroup: FilterGroupSchema.optional(),
      dimensionWeight: z.number().min(0).max(1).optional().default(0),
    }),
    responses: {
      200: SingleReliablePartsResponseSchema,
      403: z.object({ message: z.string() }),
    },
    summary: "Search for reliable similarParts parts",
  },

  search: {
    method: "GET",
    path: "/quote-request-lines/search",
    query: z.object({
      page: z.number().nonnegative().optional().default(1),
      search: z.string().optional(),
      sort: z.string().optional(),
    }),
    responses: {
      200: SearchQuoteRequestLinesResponseSchema,
    },
    summary: "Search for quote request lines",
  },

  duplicate: {
    method: "POST",
    path: "/quote-request-lines/:id/duplicate",
    responses: {
      200: DuplicateQuoteRequestLineResponseSchema,
    },
    body: z.object({
      useBatch: z.boolean(),
    }),
  },

  delete: {
    method: "DELETE",
    path: "/quote-request-lines/:id",
    responses: {
      200: QuoteRequestLineOptionalDefaultsSchema.merge(
        z.object({ reworkRatio: z.number().nullable() }),
      ),
    },
    body: z.object({}).optional(),
  },

  deleteMany: {
    method: "POST",
    path: "/quote-request-lines/delete-many",
    body: z.object({
      ids: z.array(z.string().uuid()),
    }),
    responses: {
      200: z.object({
        deletedIds: z.array(z.string().uuid()),
        failedIds: z.array(z.string().uuid()).optional(),
        quoteRequestId: z.string().uuid().nullable(),
      }),
    },
  },

  create: {
    method: "POST",
    path: "/quote-request-lines",
    body: z.object({
      quoteRequestId: z.string().uuid(),
      payload: z.object({
        quantity: z.number().int().positive().default(1),
        materialGrade: MaterialGradeSchema.nullable(),
        materialName: z.string(),
        partId: z.string().uuid(),
        layout: LayoutSchema.omit({ id: true })
          .merge(
            z.object({
              materialLayout: MaterialLayoutSchema.omit({
                id: true,
                layoutId: true,
              }).nullable(),
            }),
          )
          .optional()
          .nullable(),
      }),
      shouldCreateChildrenQuoteRequestLines: z.boolean().optional().default(true),
    }),
    responses: {
      201: QuoteRequestLineSchema,
    },
  },
  updateMaterial: {
    method: "PATCH",
    path: "/quote-request-lines/:id/material",
    body: z.object({
      materialName: z.string(),
      materialGrade: z.union([MaterialGradeSchema, z.null()]),
    }),
    responses: {
      200: z.object({
        hasPredictionBeenCalled: z.boolean(),
        updatedQuoteRequestLines: z.array(
          z.object({
            id: z.string(),
            partId: z.string(),
            quoteRequestId: z.string(),
            materialGrade: MaterialGradeSchema,
            materialName: z.string(),
          }),
        ),
        skippedQuoteRequestLineIds: z.array(z.string()).optional(),
      }),
    },
  },
  bulkUpdateMaterial: {
    method: "PATCH",
    path: "/quote-request-lines/bulkUpdateMaterial",
    body: z.object({
      ids: z.array(z.string()),
      materialName: z.string(),
      materialGrade: z.union([MaterialGradeSchema, z.null()]),
    }),
    responses: {
      200: z.object({
        hasPredictionBeenCalled: z.boolean(),
        updatedQuoteRequestLines: z.array(
          z.object({
            id: z.string(),
            partId: z.string(),
            quoteRequestId: z.string(),
            materialGrade: MaterialGradeSchema,
            materialName: z.string(),
          }),
        ),
        skippedQuoteRequestLineIds: z.array(z.string()).optional(),
      }),
    },
  },
  updateQuantity: {
    method: "PATCH",
    path: "/quote-request-lines/:id/quantity",
    body: z.object({
      quantity: z.number().int().positive(),
    }),
    responses: {
      200: z.object({
        updatedQuoteRequestLines: z.array(
          z.object({
            id: z.string(),
            partId: z.string(),
            quoteRequestId: z.string(),
          }),
        ),
        skippedQuoteRequestLineIds: z.array(z.string()).optional(),
      }),
    },
  },
  bulkUpdateQuantity: {
    method: "PATCH",
    path: "/quote-request-lines/bulkUpdateQuantity",
    body: z.object({
      ids: z.array(z.string()),
      quantity: z.number().int().positive(),
    }),
    responses: {
      200: z.object({
        updatedQuoteRequestLines: z.array(
          z.object({
            id: z.string(),
          }),
        ),
        skippedQuoteRequestLineIds: z.array(z.string()).optional(),
      }),
    },
  },
  bulkUpdatePartType: {
    method: "PATCH",
    path: "/quote-request-lines/bulkUpdatePartType",
    body: z.object({
      ids: z.array(z.string()),
      partType: PartTypeSchema,
      partSubType: z.string().nullable(),
    }),
    responses: {
      200: z.object({
        results: z.array(
          z.object({
            quoteRequestLineId: z.string(),
            partId: z.string(),
            part: ExtendedPartSchema,
            shouldAnalyzePart: z.boolean(),
            shouldPredictAfterAnalyze: z.boolean(),
          }),
        ),
      }),
    },
  },
  add2dFile: {
    method: "POST",
    path: "/quote-request-lines/:id/files/2d",
    body: z.object({
      file: FileSchema.pick({ id: true }),
    }),
    responses: {
      200: z.object({}),
    },
  },
  updateMaterialAndQuantity: {
    method: "PATCH",
    path: "/quote-request-lines/:id/updateMaterialAndQuantity",
    body: z.object({
      materialGrade: MaterialGradeSchema,
      quantity: z.number().int().positive(),
      shouldSearchForRelevantMaterial: z.boolean().optional().default(true),
    }),
    responses: {
      200: z.object({
        hasPredictionBeenCalled: z.boolean(),
        updatedQuoteRequestLines: z.array(
          z.object({
            id: z.string(),
            partId: z.string(),
            quoteRequestId: z.string(),
          }),
        ),
      }),
    },
  },
  predict: {
    method: "POST",
    path: "/quote-request-lines/:id/predict",
    body: z.object({
      shouldHandleAssembly: z.boolean(),
      failedOrUnStartedChildrenOnly: z.boolean().optional(),
    }),
    responses: {
      200: z.object({
        quoteRequestLinesIds: z.string().uuid().array(),
      }),
    },
  },
  predictMaterial: {
    method: "POST",
    path: "/quote-request-lines/:id/predict-material",
    body: z.object({
      materialGrade: MaterialGradeSchema,
      quantity: z.coerce.number().int().positive(),
    }),
    responses: {
      200: MaterialLayoutSchema.nullable(),
    },
  },
  bulkPredictSurfaceTreatment: {
    method: "POST",
    path: "/quote-request-lines/surface-treatments/predict",
    body: z.object({
      quoteRequestLineIds: z.string().uuid().array(),
      surfaceTreatmentDescriptions: z.string().array(),
    }),
    responses: {
      200: z.record(z.string().uuid().array()),
    },
  },
  bulkDeleteSurfaceTreatments: {
    method: "DELETE",
    path: "/quote-request-lines/surface-treatments",
    body: z.object({
      quoteRequestLineIds: z.string().uuid().array(),
    }),
    responses: {
      200: z.string().uuid().array(),
    },
  },
  getSurfaceTreatmentsPredictions: {
    method: "GET",
    path: "/quote-request-lines/:id/surface-treatments/predictions",
    responses: {
      200: ProcessStepSchema.partial()
        .extend({
          industrialProcess: IndustrialProcessSchema,
          mode: ProcessStepModeSchema,
          index: z.number(),
        })
        .array(),
    },
  },
  bulkValidateCosting: {
    method: "POST",
    path: "/quote-request-lines/validate-costing",
    body: z.object({
      quoteRequestLineIds: z.string().uuid().array(),
    }),
    responses: {
      200: z.object({
        validatedQuoteRequestLinesIds: z.string().uuid().array(),
        unvalidatedQuoteRequestLinesIds: z.string().uuid().array(),
      }),
    },
  },
  validateCosting: {
    method: "POST",
    path: "/quote-request-lines/:id/validate-costing",
    body: null,
    responses: {
      200: z.object({
        validatedQuoteRequestLinesIds: z.string().uuid().array(),
        unvalidatedQuoteRequestLinesIds: z.string().uuid().array(),
      }),
    },
  },

  customInputs: {
    method: "GET",
    path: "/quote-request-lines/custom-inputs",
    responses: {
      200: CustomInputResponseSchema.array(),
    },
  },

  findCustomInputsBulk: {
    method: "GET",
    path: "/quote-request-lines/custom-inputs-bulk",
    query: z.object({
      quoteRequestId: z.string().uuid(),
    }),
    responses: {
      200: z.record(QuoteRequestLineSchema.shape.id, CustomInputSchema.array()),
    },
  },

  upsertCustomInput: {
    method: "POST",
    path: "/quote-request-lines/:id/custom-inputs",
    body: CustomInputSchema.omit({
      createdAt: true,
      updatedAt: true,
      quoteRequestLineId: true,
    }).extend({ id: z.string().optional() }),
    responses: {
      200: CustomInputSchema,
    },
  },

  updateMargins: {
    method: "PATCH",
    path: "/quote-request-lines/:id/margins",
    body: z.object({
      materialMarginRatio: z.number().optional(),
      productsMarginRatio: z.number().optional(),
      addedValueMarginRatio: z.number().optional(),
    }),
    responses: {
      200: QuoteRequestLineSchema,
    },
  },

  findMany: {
    method: "POST",
    path: "/quote-request-lines/bulk/find-many",
    body: z.object({
      ids: z.array(z.string().uuid()),
    }),
    responses: {
      200: FindOneQuoteRequestLineWithLatestJobSchema.array(),
    },
    summary: "Bulk fetch multiple quote request lines by IDs",
  },

  getProcessStepCatalog: {
    method: "GET",
    path: "/quote-request-lines/process-step-catalog",
    query: z.object({
      materialGrade: MaterialGradeSchema,
    }),
    responses: {
      200: z.array(
        z.object({
          industrialProcess: IndustrialProcessSchema,
          description: z.string().nullable().optional(),
          processKey: z.string().nullable(),
          index: z.number(),
          mode: ProcessStepModeSchema,
        }),
      ),
    },
    summary: "Fetch available process step catalog without ML inference",
  },

  addPricedProcessStep: {
    method: "POST",
    path: "/quote-request-lines/:id/add-priced-process-step",
    body: z.object({
      industrialProcess: IndustrialProcessSchema,
      description: z.string().nullable(),
      processKey: z.string().nullable(),
      index: z.number(),
      materialGrade: MaterialGradeSchema,
    }),
    responses: {
      200: ProcessStepSchema.extend({
        materialLayout: MaterialLayoutSchema.nullable(),
        productLines: ProductLineSchema.array(),
      }),
    },
    summary: "Create a process step with pricing from inference in a single operation",
  },

  findOverthickness: {
    method: "GET",
    path: "/quote-request-lines/:id/overthickness",
    responses: {
      200: z.object({
        turning: z.object({ length: z.number(), diameter: z.number() }),
        machining: z.object({ length: z.number(), width: z.number(), height: z.number() }),
        sheet: z.object({ length: z.number(), width: z.number(), height: z.number() }),
      }),
    },
    summary: "Get resolved overthickness dimensions for a quote request line",
  },
});
